import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Eye, BarChart3, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { logError } from '../../lib/logger';
import { TEAM_META } from './ArticleCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => {
  let token = localStorage.getItem('dcsr_poll_token');
  if (!token) {
    token = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('dcsr_poll_token', token);
  }
  return token;
};

const accentFor = (teamKey) => {
  const c = (TEAM_META[teamKey] || {}).color;
  return c === '#000000' ? '#BF0A30' : c || '#BF0A30';
};

export const FanPoll = ({ teamKey }) => {
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const accent = accentFor(teamKey);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/team-hub/polls/active/${teamKey}`, {
        params: { token: getToken() },
      });
      setPoll(response.data.poll);
      setVoted(!!response.data.already_voted);
    } catch (error) {
      logError('poll load failed:', error);
    }
  }, [teamKey]);

  useEffect(() => { load(); }, [load]);

  if (!poll) return null;

  const vote = async (index) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/team-hub/polls/${poll.id}/vote`, {
        option_index: index,
        token: getToken(),
      });
      setPoll(response.data.poll);
      setVoted(true);
    } catch (error) {
      logError('vote failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`fan-poll-${teamKey}`} className="bg-white border border-slate-200">
      <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: accent }}>
        <BarChart3 className="w-4 h-4 text-white" />
        <h3 className="font-heading text-sm text-white uppercase tracking-wide">Fan Poll</h3>
      </div>
      <div className="p-4">
        <p className="font-serif-head font-bold text-base text-slate-900 leading-snug">{poll.question}</p>
        <div className="mt-3 space-y-2">
          {poll.options.map((opt) =>
            voted ? (
              <div key={opt.index} data-testid={`poll-result-${opt.index}`} className="relative border border-slate-200 overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-slate-100" style={{ width: `${opt.pct}%` }} />
                <div className="relative flex items-center justify-between px-3 py-1.5">
                  <span className="text-sm text-slate-800">{opt.text}</span>
                  <span className="text-xs font-bold text-slate-600 tabular-nums">{opt.pct}%</span>
                </div>
              </div>
            ) : (
              <button
                key={opt.index}
                data-testid={`poll-option-${opt.index}`}
                onClick={() => vote(opt.index)}
                disabled={submitting}
                className="w-full text-left border border-slate-300 px-3 py-1.5 text-sm text-slate-800 hover:border-slate-900 hover:bg-slate-50 transition-colors"
              >
                {opt.text}
              </button>
            )
          )}
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          {voted ? `${poll.total_votes} vote${poll.total_votes === 1 ? '' : 's'} · thanks for voting` : 'Tap an option to vote — no account needed'}
        </p>
      </div>
    </div>
  );
};

export const Storylines = ({ teamKey }) => {
  const [items, setItems] = useState([]);
  const accent = accentFor(teamKey);

  useEffect(() => {
    axios
      .get(`${API}/team-hub/storylines/${teamKey}`)
      .then((r) => setItems(r.data.storylines || []))
      .catch((error) => logError('storylines load failed:', error));
  }, [teamKey]);

  if (items.length === 0) return null;

  return (
    <div data-testid={`storylines-${teamKey}`} className="bg-white border border-slate-200">
      <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: accent }}>
        <Eye className="w-4 h-4 text-white" />
        <h3 className="font-heading text-sm text-white uppercase tracking-wide">What We're Watching</h3>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((s) => (
          <div key={s.id} className="px-4 py-3">
            <p className="text-sm font-bold text-slate-900">{s.title}</p>
            {s.note && <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{s.note}</p>}
            {s.article_id && (
              <Link
                to={`/article/${s.article_id}`}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide mt-1 hover:underline"
                style={{ color: accent }}
              >
                Read Our Coverage <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
